import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { portalTournamentAPI } from '../../../services/api';

/**
 * PortalSideActionTab - Shows contests and wallet balance for this tournament.
 * Players can see which contests they've joined and the current pot sizes.
 */
const PortalSideActionTab = () => {
  const { tournamentId } = useParams();
  const { tournament, portalStatus } = useOutletContext();
  const [contests, setContests] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletId, setWalletId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joiningContestId, setJoiningContestId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchSideAction = async () => {
    try {
      const res = await portalTournamentAPI.getSideAction(tournamentId);
      const data = res.data?.data || {};
      setContests(data.contests || []);
      setWalletBalance(data.wallet?.balance);
      setWalletId(data.wallet?._id);
    } catch (err) {
      console.error('Failed to load side action:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSideAction();
  }, [tournamentId]);

  const handleJoinContest = async (contestId, contestName, entryFee) => {
    setShowConfirm({ contestId, contestName, entryFee });
  };

  const confirmJoinContest = async () => {
    const { contestId, contestName, entryFee } = showConfirm;
    setShowConfirm(null);
    setJoiningContestId(contestId);
    setMessage(null);

    try {
      const res = await portalTournamentAPI.joinContest(tournamentId, contestId);

      if (res.data.success) {
        setMessage({ type: 'success', text: `Successfully joined ${contestName}!` });
        // Refresh contests to show updated status
        await fetchSideAction();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to join contest. Please try again.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setJoiningContestId(null);
      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-6 w-6 border-4 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const isPostRound = ['completed', 'recap-posted'].includes(portalStatus);

  return (
    <div className="space-y-4">
      {/* Message Banner */}
      {message && (
        <div className={`rounded-lg p-4 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowConfirm(null)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Join {showConfirm.contestName}?
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Entry fee of <span className="font-semibold">${showConfirm.entryFee.toFixed(2)}</span> will be deducted from your wallet.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmJoinContest}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Join Contest
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Wallet Balance */}
      {walletBalance != null && (
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 text-white shadow-md">
          <p className="text-green-100 text-xs uppercase tracking-wide mb-1">
            Wednesday Side Action Wallet
          </p>
          <p className="text-2xl font-bold">${walletBalance.toFixed(2)}</p>
          {walletId && (
            <Link
              to={`/wallets/${walletId}`}
              className="text-green-200 text-xs mt-2 inline-block hover:text-white"
            >
              View Transaction History
            </Link>
          )}
        </div>
      )}

      {/* Contests */}
      {contests.length > 0 ? (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-700">Side Action</h3>
          {contests.map((contest) => {
            const isJoining = joiningContestId === contest._id;

            return (
              <div
                key={contest._id}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900 text-sm">{contest.name}</h4>
                  {contest.isJoined ? (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                      {contest.participationStatus === 'paid' ? 'Joined' : 'Pending'}
                    </span>
                  ) : contest.canJoin ? (
                    <button
                      onClick={() => handleJoinContest(contest._id, contest.name, contest.entryFee)}
                      disabled={isJoining}
                      className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isJoining ? 'Joining...' : 'Join'}
                    </button>
                  ) : null}
                </div>

                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {contest.entryFee != null && (
                    <span>Entry: ${contest.entryFee.toFixed(2)}</span>
                  )}
                  {contest.accounting?.totalPot != null && (
                    <span>Pot: ${contest.accounting.totalPot.toFixed(2)}</span>
                  )}
                  {contest.participantCount != null && (
                    <span>{contest.participantCount} players</span>
                  )}
                </div>

                {/* Payouts visible after completed */}
                {isPostRound && contest.accounting?.payouts && contest.accounting.payouts.length > 0 && (
                  <div className="mt-3 border-t border-gray-100 pt-2">
                    <p className="text-xs font-medium text-gray-600 mb-1">Payouts</p>
                    {contest.accounting.payouts.map((payout, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-700">{payout.playerName}</span>
                        <span className="font-medium text-green-600">
                          ${payout.amount?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {contest.status && (
                  <p className="text-xs text-gray-400 mt-2 capitalize">
                    Status: {contest.status.replace(/_/g, ' ')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-3xl mb-2">🏆</div>
          <h3 className="font-medium text-gray-700 mb-1">No Side Action</h3>
          <p className="text-sm text-gray-500">
            No contests have been set up for this tournament yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default PortalSideActionTab;
